// Gravity among the bodies the server lists (bodies.py): the pull on a point, the potential, the orbit a velocity makes, and the Lagrange points of every parent and child. No three.js here.
//
// Everything is in the star's frame (the star at the origin, universe axes), metres and ticks: GM in m^3 per tick^2, so an acceleration is m per tick^2 and a velocity m per tick. The bodies
// are not simulated: the server moves them on circles (bodies.ts, `absAll`), and the pull on a point is the sum of theirs, each at its place at the tick asked about. A point mass outside
// a body's radius, a uniform sphere inside it (so the pull is continuous and finite at the centre).
//
// LAGRANGE POINTS. Bodies.state (bodies.ts) puts each parent and child on their circles about their common centre of mass, so a pair is the classical circular restricted problem: in the
// frame that turns with the pair (the centre of mass at the origin, the parent at -m2 and the child at 1 - m2 in lengths of their separation, m2 = M_child / (GM_parent + M_child), the child
// with its own moons) a point at rest feels
//     F = -m1 (x - x1) / |x - x1|^3  -  m2 (x - x2) / |x - x2|^3  +  x          (in the orbit plane, the rate being the pair's own)
// and the Lagrange points are its zeros, found by Newton's method from the usual starting places (a third of the Hill radius, the equilateral points). With the parent pinned
// instead (as the circles alone have it) the equilateral points are not equilibria, because nothing answers the child's tug on the parent.
import { add, scale, cross, len, norm } from './bodies';
import type { Bodies, V3 } from './bodies';

/** The pull (m per tick^2) of every body with a GM at `p`, the bodies being at `abs` (the star's frame, `list` order). */
export function pullAt(B: Bodies, abs: V3[], p: ArrayLike<number>, out: V3 = [0, 0, 0]): V3 {
  let ax = 0, ay = 0, az = 0;
  for (let i = 0; i < B.list.length; i++) {
    const b = B.list[i], gm = b.gm;
    if (!gm) continue;
    const dx = abs[i][0] - p[0], dy = abs[i][1] - p[1], dz = abs[i][2] - p[2];
    const r = Math.hypot(dx, dy, dz), rr = Math.max(r, b.radius, 1);
    const k = gm / (rr * rr * rr);
    ax += dx * k; ay += dy * k; az += dz * k;
  }
  out[0] = ax; out[1] = ay; out[2] = az;
  return out;
}

/** The body that pulls hardest at `p`: its index in `list` and the size of its pull (m per tick^2). */
export function strongest(B: Bodies, abs: V3[], p: ArrayLike<number>): { i: number; a: number } {
  let bi = -1, ba = 0;
  for (let i = 0; i < B.list.length; i++) {
    const b = B.list[i];
    if (!b.gm) continue;
    const r = Math.max(Math.hypot(abs[i][0] - p[0], abs[i][1] - p[1], abs[i][2] - p[2]), b.radius, 1);
    const a = b.gm / (r * r);
    if (a > ba) { ba = a; bi = i; }
  }
  return { i: bi, a: ba };
}

/** The gravitational potential at `p` (m^2 per tick^2), zero at infinity. */
export function potentialAt(B: Bodies, abs: V3[], p: ArrayLike<number>): number {
  let phi = 0;
  for (let i = 0; i < B.list.length; i++) {
    const b = B.list[i], gm = b.gm;
    if (!gm) continue;
    const r = Math.hypot(abs[i][0] - p[0], abs[i][1] - p[1], abs[i][2] - p[2]);
    phi -= r >= b.radius ? gm / Math.max(r, 1) : gm * (3 * b.radius * b.radius - r * r) / (2 * b.radius * b.radius * b.radius);
  }
  return phi;
}

export interface OrbitElements {
  /** The orbit closes (negative energy). */
  bound: boolean;
  /** Specific energy (m^2 per tick^2). */
  energy: number;
  /** Eccentricity. */
  e: number;
  /** Closest and farthest distance from the centre (m); apo is Infinity when it does not close. */
  peri: number; apo: number;
  /** Period (ticks); Infinity when it does not close. */
  period: number;
  /** Distance and speed now (m, m per tick). */
  r: number; v: number;
}

/** The two-body orbit of a point at `rel` with velocity `vel` (relative to the body) about a body of `gm`. */
export function orbitElements(gm: number, rel: ArrayLike<number>, vel: ArrayLike<number>): OrbitElements {
  const r = len(rel), v = len(vel);
  const energy = 0.5 * v * v - gm / Math.max(r, 1e-9);
  const h = len(cross(rel, vel));
  const e = Math.sqrt(Math.max(0, 1 + 2 * energy * h * h / (gm * gm)));
  const peri = h * h / (gm * (1 + e));
  if (energy < 0) {
    const a = -gm / (2 * energy);
    return { bound: true, energy, e, peri, apo: a * (1 + e), period: 2 * Math.PI * Math.sqrt(a * a * a / gm), r, v };
  }
  return { bound: false, energy, e, peri, apo: Infinity, period: Infinity, r, v };
}

export interface LagrangePoint {
  /** 1 to 5. */
  n: number;
  /** The pair: the parent's and the child's index in the registry's `list`. */
  parent: number; child: number;
  /** Where it is (the star's frame, metres). */
  pos: V3;
}

/** Newton's method on the pair's force (lengths of the separation, the parent at -m2, the child at 1 - m2); null if it does not settle. */
function solvePoint(m1: number, m2: number, x0: number, y0: number): [number, number] | null {
  const xp = -m2, xc = 1 - m2;
  let x = x0, y = y0;
  for (let it = 0; it < 60; it++) {
    const d1 = x - xp, d2 = x - xc;
    const r1 = Math.hypot(d1, y), r2 = Math.hypot(d2, y);
    if (!(r1 > 1e-6) || !(r2 > 1e-6)) return null;
    const i1 = 1 / (r1 * r1 * r1), i2 = 1 / (r2 * r2 * r2), i51 = i1 / (r1 * r1), i52 = i2 / (r2 * r2);
    const fx = -m1 * d1 * i1 - m2 * d2 * i2 + x, fy = -m1 * y * i1 - m2 * y * i2 + y;
    const f0 = Math.abs(fx) + Math.abs(fy);
    if (f0 < 1e-14) return [x, y];
    const jxx = -m1 * (i1 - 3 * d1 * d1 * i51) - m2 * (i2 - 3 * d2 * d2 * i52) + 1;
    const jxy = 3 * m1 * d1 * y * i51 + 3 * m2 * d2 * y * i52;
    const jyy = -m1 * (i1 - 3 * y * y * i51) - m2 * (i2 - 3 * y * y * i52) + 1;
    const det = jxx * jyy - jxy * jxy;
    if (!(Math.abs(det) > 1e-18)) return null;
    let sx = (fx * jyy - fy * jxy) / det, sy = (jxx * fy - jxy * fx) / det;
    const sl = Math.hypot(sx, sy);
    if (sl > 0.2) { sx *= 0.2 / sl; sy *= 0.2 / sl; }          // a short step at most: the starting places are close
    x -= sx; y -= sy;
  }
  return null;
}

/** The Lagrange points of every parent and child in the registry (bodies that move on a circle about a parent with a GM), the bodies being at `abs` (their places at `tick`). */
export function lagrangePoints(B: Bodies, abs: V3[], tick: number): LagrangePoint[] {
  const out: LagrangePoint[] = [];
  for (let ci = 0; ci < B.list.length; ci++) {
    const cc = B.circleOf(ci);
    const child = B.list[ci];
    if (!cc || !child.gm) continue;
    const pi = cc.parent, parent = B.list[pi];
    if (!parent || !parent.gm) continue;
    const c = cc.circle, ph = c.phi0 + c.w * tick, co = Math.cos(ph), si = Math.sin(ph);
    const sep: V3 = [c.a * (co * c.e1[0] + si * c.e2[0]), c.a * (co * c.e1[1] + si * c.e2[1]), c.a * (co * c.e1[2] + si * c.e2[2])];
    const a = len(sep);
    if (!(a > 0)) continue;
    const u = scale(sep, 1 / a);
    const nrm = norm(cross(c.e1, c.e2));
    const v = cross(nrm, u);
    const mc = B.subtreeGm(ci), mt = parent.gm + mc;
    const m2 = mc / mt, m1 = 1 - m2, h = Math.cbrt(m2 / 3);
    const centre = add(abs[pi], scale(sep, m2));                                   // the pair's centre of mass
    const guess: [number, number, number][] = [
      [1, 1 - m2 - h, 0], [2, 1 - m2 + h, 0], [3, -(1 + 5 * m2 / 12), 0],
      [4, 0.5 - m2, Math.sqrt(3) / 2], [5, 0.5 - m2, -Math.sqrt(3) / 2],
    ];
    for (const [n, gx, gy] of guess) {
      const s = solvePoint(m1, m2, gx, gy);
      if (!s) continue;
      out.push({ n, parent: pi, child: ci, pos: add(centre, add(scale(u, s[0] * a), scale(v, s[1] * a))) });
    }
  }
  return out;
}
